import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { SPACINGS } from '../../utils/style';
import { Text } from '../../typography';
import chroma from 'chroma-js';

type InputType = 'default' | 'currency' | 'number' | 'password';

function handleInputType(type: InputType) {
  switch (type) {
    case 'currency':
    case 'number':
      return 'number';
    case 'password':
      return 'password';
    default:
      return '';
  }
}

const tooltipDimension = '57px';

const InputWrapper = styled.div<{ tooltip: boolean }>`
  position: relative;
  width: ${(p) => p.theme.dimensions.inputWidth};

  .currency {
    display: inline-flex;
    position: absolute;
    align-items: center;
    top: 0;
    padding: ${SPACINGS[6]} ${SPACINGS[2]} ${SPACINGS[1]};
  }

  .label {
    position: absolute;
    top: 0;
    padding: 0 ${SPACINGS[2]};
    padding-right: ${(p) =>
      p.tooltip ? `calc(${tooltipDimension} + ${SPACINGS[2]})` : SPACINGS[2]};
    pointer-events: none;
    transform-origin: left;
    transition: transform 0.2s;

    &.up {
      transform: translateY(0) scale(0.8);
    }

    &.down {
      transform: translateY(${SPACINGS[5]}) scale(1);
    }
  }

  .tooltip {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: ${tooltipDimension};
  }
`;

type InputStyledProps = {
  error: boolean;
  componentType: InputType;
  tooltip: boolean;
  baseColor: string;
  mainColor: string;
  accentColor: string;
  inactiveColor: string;
  dangerColor: string;
  backgroundColor: string;
};
const InputStyled = styled.input<InputStyledProps>`
  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;

  background-clip: padding-box;

  margin: 0;
  margin-bottom: ${(p) => (p.error ? SPACINGS[2] : 0)};
  padding: ${SPACINGS[6]} ${SPACINGS[2]} ${SPACINGS[1]};
  padding-right: ${(p) =>
    p.tooltip ? `calc(${tooltipDimension} + ${SPACINGS[2]})` : SPACINGS[2]};

  background-color: ${(p) => chroma(p.baseColor).alpha(0.1).hex()};
  width: 100%;
  border: none;
  border-bottom: 1px solid ${(p) => p.baseColor};
  border-radius: 4px 4px 0 0;

  color: ${(p) => p.mainColor};
  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-size: ${sizesMap['base'].size};
  line-height: ${textLineHeight};
  text-align: ${(p) =>
    handleInputType(p.componentType) === 'number' ? 'right' : 'left'};

  &::placeholder {
    color: ${(p) => p.baseColor};
  }

  &:disabled {
    color: ${(p) => p.inactiveColor};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:read-only {
    color: ${(p) => p.inactiveColor};
    &:hover {
      cursor: not-allowed;
    }
  }

  &:not(:disabled) {
    &:hover,
    &:focus {
      background-color: ${(p) => chroma(p.accentColor).alpha(0.1).hex()};
      outline: none;
      border-bottom: 1px solid ${(p) => p.accentColor};
    }
  }

  &[type='number']::-webkit-inner-spin-button,
  &[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

export type InputProps = {
  name: string;
  label?: string;
  error?: string | boolean;
  inputRef?:
    | ((instance: HTMLInputElement | null) => void)
    | React.RefObject<HTMLInputElement>;
  style?: React.CSSProperties;
  defaultValue?: string;
  value?: string;
  onChange?: ((event: React.ChangeEvent<HTMLInputElement>) => void) | undefined;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  type?: InputType;
  currency?: string;
  placeholder?: string;
  tooltip?: React.ReactNode;
  textColor?: string;
  baseColor?: string;
  mainColor?: string;
  accentColor?: string;
  inactiveColor?: string;
  dangerColor?: string;
  backgroundColor?: string;
  initialIsLabelUp?: boolean;
};

function Input(props: InputProps) {
  const theme = useTheme();
  const {
    name,
    label,
    inputRef,
    error,
    style,
    defaultValue,
    value,
    onChange,
    autoFocus,
    disabled,
    readOnly,
    type = 'default',
    currency,
    placeholder,
    tooltip,
    textColor,
    baseColor = theme.colors.secondary80w,
    mainColor = theme.colors.black,
    accentColor = theme.colors.primary,
    inactiveColor = theme.colors.secondary60w,
    dangerColor = theme.colors.danger,
    backgroundColor = theme.colors.white,
    initialIsLabelUp: initialIsLabelUpProp,
  } = props;

  const errorStyle = React.useMemo(() => ({ color: theme.colors.danger }), [
    theme,
  ]);

  const innerRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (typeof inputRef === 'function') inputRef(innerRef.current);
  }, [inputRef]);

  const getLabelUp = React.useCallback(
    (options?: { focused?: boolean; initial?: boolean }) => {
      if (options?.initial) return options.initial;
      if (options?.focused) return true;
      if (placeholder) return true;
      if (value === '') return false;
      if (innerRef?.current?.value === '') return false;

      if (defaultValue) return true;
      if (value) return true;
      if (innerRef?.current?.value) return true;
      return false;
    },
    [defaultValue, placeholder, value]
  );

  const [isFocused, setIsFocused] = React.useState(false);
  const [isLabelUp, setIsLabelUp] = React.useState(getLabelUp());
  const [initialIsLabelUp, setInitialIsLabelUp] = React.useState(
    initialIsLabelUpProp
  );

  function handleFocus() {
    setInitialIsLabelUp(undefined);
    setIsFocused(true);
    setIsLabelUp(getLabelUp({ focused: true }));
  }

  function handleBlur() {
    setIsFocused(false);
    setIsLabelUp(getLabelUp());
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (onChange) onChange(e);
    if (!isLabelUp) setIsLabelUp(getLabelUp());
  }

  React.useEffect(() => {
    setIsLabelUp(
      getLabelUp({
        focused: isFocused,
        initial: initialIsLabelUp,
      })
    );
  }, [value, getLabelUp, isFocused, initialIsLabelUp]);

  return (
    <InputWrapper tooltip={Boolean(tooltip)} style={style}>
      {label && (
        <Text
          className={`label ${isLabelUp ? 'up' : 'down'}`}
          margin="none"
          type="label"
          htmlFor={name}
        >
          {label}
        </Text>
      )}
      {type === 'currency' && currency && isLabelUp && (
        <Text className="currency" style={{ color: textColor }} margin="none">
          {currency}
        </Text>
      )}
      <InputStyled
        componentType={type}
        type={handleInputType(type)}
        id={name}
        name={name}
        error={Boolean(error)}
        ref={innerRef}
        defaultValue={defaultValue}
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoFocus={autoFocus}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        baseColor={baseColor}
        mainColor={textColor ?? mainColor}
        accentColor={accentColor}
        inactiveColor={inactiveColor}
        dangerColor={dangerColor}
        backgroundColor={backgroundColor}
        tooltip={Boolean(tooltip)}
      />
      {tooltip && <div className="tooltip">{tooltip}</div>}
      {typeof error === 'string' && (
        <Text id={`${name}-error`} style={errorStyle} margin="none">
          {error}
        </Text>
      )}
    </InputWrapper>
  );
}

export default React.memo(Input);
